import { LogsInitConfiguration } from '@datadog/browser-logs'
import { RumInitConfiguration } from '@datadog/browser-rum-slim'
import { Intersection } from 'utility-types'
import { getBrandObject, getUrlParamByName } from 'embed/utils'
import { captureNoApiTokenWarning, captureNoCompanyUuidWarning } from 'embed/constants/errors'
import { Brand, CreditCardFlow } from '@evenfinancial/even-ts-static'
import { DEFAULT_SAMPLE_RATE as DATADOG_DEFAULT_SAMPLE_RATE } from 'embed/services/Datadog/constants'

// Generate an embed or consumer url
const generateUrl = (isDevelopment: boolean, isStage: boolean, isEmbed: boolean, brand?: Brand) => {
  const subdomainParts: string[] = []
  if (isDevelopment) {
    subdomainParts.push('dev')
  }

  if (isStage) {
    subdomainParts.push('staging')
  }

  if (isEmbed) {
    subdomainParts.push('embed')
  }

  if (!isEmbed && brand === Brand.Even) {
    subdomainParts.push('consumer')
  }

  const subdomain = subdomainParts.join('-')
  const domain = getBrandObject(brand).domain
  const path = !isEmbed && brand === Brand.LeapLife ? '/partner' : ''

  return `https://${subdomain.length === 0 ? domain : `${subdomain}.${domain}`}${path}`
}

const apiTokenFromUrlParams = getUrlParamByName(document.location.search, 'access_token')

const companyUuidFromUrlParams = getUrlParamByName(document.location.search, 'company_uuid')

const devApiToken = apiTokenFromUrlParams || process.env.DEV_API_TOKEN
const devApiUrl = process.env.DEV_BASE_API_URL || 'https://dev-api.evenfinancial.com'
const devCompanyUuid = companyUuidFromUrlParams || process.env.DEV_DEFAULT_COMPANY_UUID
const devConsumerApiUrl = process.env.DEV_CONSUMER_API_URL || 'https://dev.consumer.evenfinancial.com'
const devEvenConsumerPort = process.env.DEV_EVEN_CONSUMER_PORT || 3001
const devEvenEmbedPort = process.env.DEV_EVEN_EMBED_PORT || 3000
const devPartnerpageUrl = process.env.DEV_BASE_PARTNERPAGE_URL || 'http://dev-partnerpage.evenfinancial.com'
const devTrackingUrl = process.env.DEV_BASE_TRACKING_URL || 'https://dev-evtid.evenfinancial.com'
const devUnleashUrl = process.env.DEV_UNLEASH_PROXY_URL || `https://fiona.com/_svc/unleash-proxy/proxy`

const prodApiToken = apiTokenFromUrlParams || process.env.PROD_API_TOKEN
const prodApiUrl = process.env.PROD_BASE_API_URL || 'https://api.evenfinancial.com'
const prodCompanyUuid = companyUuidFromUrlParams || process.env.PROD_DEFAULT_COMPANY_UUID
const prodConsumerApiUrl = process.env.PROD_CONSUMER_API_URL || 'https://consumer.evenfinancial.com'
const prodPartnerpageUrl = process.env.PROD_BASE_PARTNERPAGE_URL || 'https://partnerpage.evenfinancial.com'
const prodTrackingUrl = process.env.PROD_BASE_TRACKING_URL || 'https://evtid.evenfinancial.com'
const prodUnleashUrl = process.env.PROD_UNLEASH_PROXY_URL || `https://fiona.com/_svc/unleash-proxy/proxy`

const googleMapsApiToken = process.env.GOOGLE_API_KEY || 'AIzaSyAXBIeGOpxAZNnfptILhAIJAtDR6WcWqHw'
const maxMindKey = 'MTM2NTA1OkhKZENOaVNLR3hEcg=='
const plaidPublicKey = process.env.PLAID_PUBLIC_KEY || '7178c00a1dd843aef60ff44678ecf2'
const unleashKey = process.env.UNLEASH_PROXY_KEY || 'dum-dum-key-because-this-file-is-public '

export const localhostEmbedAddressRegExp = new RegExp(
  `^https?:\/\/localhost:[${devEvenConsumerPort}${devEvenEmbedPort}]`,
)

const shouldUseDev = (doc) =>
  !!(
    doc &&
    doc.currentScript &&
    doc.currentScript.src &&
    (/^https?:\/\/[^\.]*dev[^\.]*/.test((doc.currentScript as HTMLScriptElement).src) ||
      /^https?:\/\/localhost/.test((doc.currentScript as HTMLScriptElement).src))
  )

const isHTMLScriptElement = (element: HTMLOrSVGScriptElement | null): element is HTMLScriptElement =>
  element !== null ? 'src' in element : false
const getBrand = (doc: HTMLDocument): Brand | undefined => {
  if (!isHTMLScriptElement(doc.currentScript)) {
    return
  }

  const { src } = doc.currentScript
  if (localhostEmbedAddressRegExp.test(src) || /^https?:\/\/(.*\.)?evenfinancial\.com/.test(src)) {
    return Brand.Even
  } else if (/^https?:\/\/(.*\.)?(hi)?fiona\.com/.test(src)) {
    return Brand.Fiona
  } else if (/^https?:\/\/(.*\.)?leaplife\.com/.test(src)) {
    return Brand.LeapLife
  }
}

const brandFromHost = getBrand(document)

export const isLocalhost = localhostEmbedAddressRegExp.test((document?.currentScript as HTMLScriptElement)?.src)

const shouldUseStaging = (doc) =>
  !!(
    doc &&
    doc.currentScript &&
    doc.currentScript.src &&
    (/^https?:\/\/[^\.]*staging[^\.]*/.test((doc.currentScript as HTMLScriptElement).src) ||
      /^https?:\/\/localhost/.test((doc.currentScript as HTMLScriptElement).src))
  )

const isDev = shouldUseDev(document)
const isStaging = shouldUseStaging(document)

export const defaultCreditScoreUrl =
  'https://offers.evenfinancial.com/partner/ref/516a1f88-3d04-4581-b366-0f5d939ee72d/9a87cee4-8df5-4c6f-952d-82743552a4e4'

const apiToken = isDev ? devApiToken : prodApiToken
const baseApiUrl = isDev ? devApiUrl : prodApiUrl
const baseConsumerUrl = process.env.BASE_CONSUMER_URL || generateUrl(isDev, isStaging, false, brandFromHost)
const basePartnerpageUrl = isDev ? devPartnerpageUrl : prodPartnerpageUrl
const baseTrackingUrl = isDev ? devTrackingUrl : prodTrackingUrl
const unleashProxyUrl = isDev ? devUnleashUrl : prodUnleashUrl
const companyUuid = isDev ? devCompanyUuid : prodCompanyUuid
const consumerApiUrl = isDev ? devConsumerApiUrl : prodConsumerApiUrl
const fionaBaseApiUrl = isDev ? 'https://dev.fiona.com' : 'https://fiona.com'
const releaseVersion = process.env.VERSION || process.env.COMMIT_PREFIX
const plaidEnv = isDev ? 'sandbox' : 'production'

if (apiTokenFromUrlParams === undefined) {
  captureNoApiTokenWarning()
}

if (companyUuidFromUrlParams === undefined) {
  captureNoCompanyUuidWarning()
}

// Checks whether clicking a credit card offer will select that offer and load the application form.
// The card's subacccount either needs to support pre-qual
// or else it needs to support pre-select and the credit card flow needs to be pre-select.
const willSelectCreditCardOffer = (
  supportsPreQual: boolean,
  supportsPreSelect: boolean,
  flow: CreditCardFlow | undefined,
) => supportsPreQual || (flow === CreditCardFlow.PreSelect && supportsPreSelect)

// Checks whether clicking a credit card offer will redirect to an external offer page.
// It will redirect just in case willSelectCreditCardOffer is false for the same arguments.
const willRedirectToExternalOfferPage = (
  supportsPreQual: boolean,
  supportsPreSelect: boolean,
  flow: CreditCardFlow | undefined,
) => !willSelectCreditCardOffer(supportsPreQual, supportsPreSelect, flow)

const ddShared: Intersection<RumInitConfiguration, LogsInitConfiguration> = {
  clientToken: process.env.DATADOG_CLIENT_TOKEN || 'pub252c1cc9d60727a65f6d66634c4cd6e1',
  env: isDev || isLocalhost ? 'development' : isStaging ? 'staging' : 'production',
  sampleRate: process.env.DATADOG_SAMPLE_RATE
    ? parseInt(process.env.DATADOG_SAMPLE_RATE, 10)
    : DATADOG_DEFAULT_SAMPLE_RATE,
  service: process.env.DATADOG_SERVICE || 'embed',
  site: process.env.DATADOG_SITE || 'datadoghq.com',
  trackSessionAcrossSubdomains: true,
  useCrossSiteSessionCookie: true,
  useSecureSessionCookie: true,
  version: releaseVersion,
}
if (process.env.DATADOG_PROXY_HOST || process.env.DATADOG_PROXY_PATH) {
  const ddProxyHost = process.env.DATADOG_PROXY_HOST ? process.env.DATADOG_PROXY_HOST : window.location.host
  const ddProxyPath = process.env.DATADOG_PROXY_PATH ? process.env.DATADOG_PROXY_PATH : '/'
  ddShared.proxyUrl = `${window.location.protocol}}//${ddProxyHost}${ddProxyPath}`
}

const ddLogs: LogsInitConfiguration = {
  ...(ddShared as LogsInitConfiguration),
}

const ddRUM: RumInitConfiguration = {
  ...(ddShared as RumInitConfiguration),
  allowedTracingOrigins: [
    devApiUrl,
    prodApiUrl,
    'https://dev-embed.evenfinancial.com',
    'https://embed.evenfinancial.com',
    'https://dev-embed.fiona.com',
    'https://embed.fiona.com',
    // @TODO: MPS-743 - Enable following two once Tracker has CORS headers enabled for RUM
    // devTrackingUrl,
    // prodTrackingUrl,
    // @TODO: MPS-744 - Enable following two once Partner Page has CORS headers enabled for RUM
    // devPartnerpageUrl,
    // prodPartnerpageUrl,
    // @TODO: MPS-745 - Enable following two once Consumer has CORS headers enabled for RUM
    // devConsumerApiUrl,
    // prodConsumerApiUrl,
  ],
  applicationId: process.env.DATADOG_APPLICATION_ID || 'ed8f12f9-905a-47c9-b3f4-5a58f246aa31',
  premiumSampleRate: 0,
  replaySampleRate: 0,
  trackInteractions: true,
}
// If not in production, override the sample rate to 100% to ease debugging.
if (ddRUM.env !== 'production') {
  ddRUM.sampleRate = 100
}

const config = {
  apiToken,
  baseApiUrl,
  basePartnerpageUrl,
  baseTrackingUrl,
  brandFromHost,
  companyUuid,
  defaultCreditScoreUrl,
  generateUrl,
  getBrand,
  googleMapsApiToken,
  isDev,
  maxMindKey,
  shouldUseDev,
  shouldUseStaging,
  willRedirectToExternalOfferPage,
  willSelectCreditCardOffer,
}

export default config

export {
  apiToken,
  baseApiUrl,
  baseConsumerUrl,
  basePartnerpageUrl,
  baseTrackingUrl,
  companyUuid,
  consumerApiUrl,
  ddLogs,
  ddRUM,
  fionaBaseApiUrl,
  generateUrl,
  brandFromHost,
  googleMapsApiToken,
  maxMindKey,
  isDev,
  isStaging,
  plaidEnv,
  plaidPublicKey,
  releaseVersion,
  shouldUseDev,
  shouldUseStaging,
  willRedirectToExternalOfferPage,
  willSelectCreditCardOffer,
  unleashKey,
  unleashProxyUrl,
}
